import { Organization, OrganizationDocument, OrganizationSettings } from '@/models/Organization';
import { toast } from 'sonner';

interface CreateOrganizationData {
  name: string;
  email: string;
  phone: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
  };
  settings?: Partial<OrganizationSettings>;
  owner: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
  };
  subscription?: {
    type: 'free' | 'basic' | 'premium';
    expiresAt: Date;
    maxEmployees?: number;
  };
}

class OrganizationService {
  private formatDateForAPI(date: string | Date | undefined): string | undefined {
    if (!date) return undefined;
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      if (isNaN(dateObj.getTime())) {
        throw new Error('Invalid date');
      }
      return dateObj.toISOString();
    } catch (error) {
      console.error('Error formatting date:', error);
      return undefined;
    }
  }

  async createOrganization(data: CreateOrganizationData): Promise<OrganizationDocument> {
    try {
      // Format the data for the registration endpoint
      const formattedData = {
        organization: {
          name: data.name,
          email: data.email,
          phone: data.phone,
          address: data.address,
          settings: data.settings
        },
        owner: {
          name: `${data.owner.firstName} ${data.owner.lastName}`,
          email: data.owner.email,
        },
        password: data.owner.password
      };

      const response = await fetch('/api/organizations/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formattedData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create organization');
      }

      return response.json();
    } catch (error) {
      console.error('Error creating organization:', error);
      throw error;
    }
  }

  async getOrganization(storeCode: string): Promise<OrganizationDocument | null> {
    try {
      const response = await fetch(`/api/organizations/${storeCode}`);
      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error('Failed to fetch organization');
      }
      return response.json();
    } catch (error) {
      console.error('Error fetching organization:', error);
      throw error;
    }
  }

  async updateOrganization(storeCode: string, data: Partial<Organization>): Promise<OrganizationDocument> {
    try {
      // Format the expiration date if it exists
      const formattedData = {
        ...data,
        subscription: data.subscription ? {
          ...data.subscription,
          expiresAt: this.formatDateForAPI(data.subscription.expiresAt)
        } : undefined
      };

      const response = await fetch(`/api/organizations/${storeCode}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formattedData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update organization');
      }

      return response.json();
    } catch (error) {
      console.error('Error updating organization:', error);
      throw error;
    }
  }

  async updateOrganizationSettings(storeCode: string, settings: Partial<OrganizationSettings>): Promise<OrganizationDocument> {
    try {
      const response = await fetch(`/api/organizations/${storeCode}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update organization settings');
      }

      return response.json();
    } catch (error) {
      console.error('Error updating organization settings:', error);
      throw error;
    }
  }

  async listOrganizations(query: { status?: string; subscriptionType?: string } = {}): Promise<OrganizationDocument[]> {
    try {
      const queryString = new URLSearchParams(query).toString();
      const response = await fetch(`/api/organizations?${queryString}`);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to list organizations');
      }

      return response.json();
    } catch (error) {
      console.error('Error listing organizations:', error);
      throw error;
    }
  }

  async deactivateOrganization(storeCode: string): Promise<void> {
    try {
      const response = await fetch(`/api/organizations/${storeCode}/deactivate`, {
        method: 'POST',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to deactivate organization');
      }
    } catch (error) {
      console.error('Error deactivating organization:', error);
      throw error;
    }
  }

  async activateOrganization(storeCode: string): Promise<void> {
    try {
      const response = await fetch(`/api/organizations/${storeCode}/activate`, {
        method: 'POST',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to activate organization');
      }
    } catch (error) {
      console.error('Error activating organization:', error);
      throw error;
    }
  }

  async updateSubscription(storeCode: string, subscriptionData: {
    type: 'free' | 'basic' | 'premium';
    expiresAt: Date;
    maxEmployees?: number;
  }): Promise<OrganizationDocument> {
    try {
      const response = await fetch(`/api/organizations/${storeCode}/subscription`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(subscriptionData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update subscription');
      }

      return response.json();
    } catch (error) {
      console.error('Error updating subscription:', error);
      throw error;
    }
  }

  async updatePassword(storeCode: string, newPassword: string): Promise<void> {
    try {
      const response = await fetch(`/api/organizations/${storeCode}/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: newPassword }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update password');
      }
    } catch (error) {
      console.error('Error updating organization password:', error);
      throw error;
    }
  }
}

export const organizationService = new OrganizationService(); 